import { Box, Button, Flex, Image, Modal, TextInput } from "@dehub/theme";
import type { AsyncDataTableRef } from "@dehub/theme";
import { MagnifyingGlassIcon, PlusIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import bz from "@/features/business/business.module.css";
import type { IBranchListRow } from "@/models/Branch";
import type { IGeneral } from "@/models/General";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { useUnsavedGuard } from "@/shared/a11y/use-unsaved-guard";
import { PageLayout } from "@/shared/layout";
import { BranchNotice } from "./components/BranchNotice";
import { BranchTable } from "./components/BranchTable";
import { BranchTypeSwitch } from "./components/BranchTypeSwitch";
import { createBranchColumns } from "./components/branch-columns";
import { BranchDrawer } from "./form/BranchDrawer";
import { useBranchPermissions } from "./permissions";
import {
  branchTypeToParam,
  paramToBranchType,
  pickBranchRecord,
  type BranchRecord,
  type DrawerMode,
  type RowActionKey,
} from "./types";

type DrawerState = { mode: DrawerMode; record?: BranchRecord; key: number; opened: boolean };

const SEARCH_DEBOUNCE_MS = 300;

export function BranchPage() {
  const { t } = useTranslation("sector");
  const { t: tCommon } = useTranslation("common");
  const [params, setParams] = useSearchParams();
  const general = useSelector((state: { general: IGeneral }) => state.general);
  const branchTypes = useMemo(() => general.branchTypes ?? [], [general.branchTypes]);
  const branchStatus = general.branchStatus ?? [];
  const perms = useBranchPermissions();

  // type (?type=), search text (?q=) and page (?page=) live in the URL: a reload or shared link lands on the same list.
  const type = paramToBranchType(
    params.get("type"),
    branchTypes.map((b) => b.code),
  );
  const query = params.get("q") ?? "";
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const updateParams = (patch: Record<string, string | null>, replace = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value === "") next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace },
    );

  const [queryInput, setQueryInput] = useState(query);
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = queryInput.trim();
      if (next !== query) updateParams({ q: next || null, page: null }, true);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryInput]);
  useEffect(() => setQueryInput(query), [query]);

  const tableRef = useRef<AsyncDataTableRef | null>(null);
  const reload = () => tableRef.current?.reload();
  const openCount = useRef(0);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [previewLogo, setPreviewLogo] = useState<{ src: string; name: string } | null>(null);
  const openerRef = useRef<string | null>(null);
  const [drawerDirty, setDrawerDirty] = useState(false);
  // Unsaved drawer edits: warn on reload/tab close and on navigation (including the type switch and back button).
  useUnsavedGuard(drawerDirty);

  const restoreFocus = () => {
    const anchor = openerRef.current;
    if (!anchor) return;
    setTimeout(() => document.querySelector<HTMLElement>(`[data-row-action="${anchor}"]`)?.focus(), 80);
  };

  const openDrawer = (mode: DrawerMode, record?: BranchRecord) => {
    openerRef.current = record ? `${record.id}:${mode === "DETAIL" ? "detail" : "edit"}` : "add";
    openCount.current += 1;
    setDrawer({ mode, record, key: openCount.current, opened: true });
  };

  const handleRowAction = (key: RowActionKey, row: IBranchListRow) =>
    openDrawer(key === "edit" ? "EDIT" : "DETAIL", pickBranchRecord(row));

  const columns = useMemo(
    () =>
      createBranchColumns({
        perms,
        branchStatus,
        branchTypes,
        t,
        tCommon,
        onRowAction: handleRowAction,
        onLogoPreview: (src, name) => setPreviewLogo({ src, name }),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perms.edit.hasAccess, perms.detail.hasAccess, branchStatus, branchTypes, t, tCommon],
  );

  const addDisabledReason = perms.add.hasAccess ? null : <MissingPermission code={perms.codes.add} />;
  const addButton = (
    <ReasonTooltip key="add" reason={addDisabledReason}>
      <Button
        data-row-action="add"
        className={bz.btn}
        leftSection={<PlusIcon size={16} weight="bold" />}
        onClick={() => openDrawer("ADD")}
      >
        {t("addBtn")}
      </Button>
    </ReasonTooltip>
  );

  return (
    <PageLayout
      title={t("page.title")}
      subTitle={t("page.subtitle")}
      breadcrumb={[{ to: "/", label: tCommon("home") }, { label: t("page.title") }]}
      extra={[addButton]}
    >
      <Flex direction="column" gap="md" className={`${bz.tokens} ${bz.page}`}>
        <BranchNotice />
        <Box className={bz.toolbar}>
          <BranchTypeSwitch
            value={type}
            types={branchTypes}
            onChange={(next) => updateParams({ type: next ? branchTypeToParam(next) : null, page: null })}
          />
          <TextInput
            className={bz.search}
            aria-label={t("searchLabel")}
            type="search"
            name="q"
            autoComplete="off"
            placeholder={t("searchPlaceholder")}
            leftSection={<MagnifyingGlassIcon size={16} aria-hidden="true" />}
            value={queryInput}
            onChange={(event) => setQueryInput(event.currentTarget.value)}
          />
        </Box>
        <BranchTable
          ref={tableRef}
          type={type}
          query={query}
          page={page}
          onPageChange={(next) => updateParams({ page: next > 1 ? String(next) : null })}
          columns={columns}
          onAdd={() => openDrawer("ADD")}
          addReason={addDisabledReason}
        />
      </Flex>

      {drawer ? (
        <BranchDrawer
          key={drawer.key}
          opened={drawer.opened}
          mode={drawer.mode}
          record={drawer.record}
          perms={perms}
          onClose={() => {
            setDrawer((d) => (d ? { ...d, opened: false } : d));
            restoreFocus();
          }}
          onDirtyChange={setDrawerDirty}
          onChanged={reload}
        />
      ) : null}

      <Modal
        title={false}
        aria-label={previewLogo?.name}
        opened={previewLogo !== null}
        onClose={() => setPreviewLogo(null)}
        padding={0}
        withCloseButton={false}
        centered
        radius="lg"
      >
        {previewLogo ? <Image src={previewLogo.src} alt={previewLogo.name} /> : null}
      </Modal>
    </PageLayout>
  );
}
